import {
  FC,
  MouseEventHandler,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import cn from 'classnames';
import { Tag, Edit, Ellipsis } from '@raydiant/raydial/components/Icons';
import Button from '@raydiant/raydial/components/Button';
import Popover from '@raydiant/raydial/components/Popover';
import Dialog from '@raydiant/raydial/components/Dialog';
import CopyIcon from './CopyIcon';
import FolderMagnifyingGlassIcon from './FolderMagnifyingGlassIcon';
import PlaylistRemoveIcon from './PlaylistRemoveIcon';
import SidebarMenuTooltip from '../../../../../components/Sidebar/SidebarMenuTooltip';

interface PlaylistItemPresentationActionsV2Props {
  onEditClick?: MouseEventHandler;
  onTagsClick?: () => void;
  onDuplicateClick?: () => void;
  onSeeInLibraryClick?: () => void;
  seeInLibraryDisabled?: boolean;
  seeInLibraryDisabledTooltip?: string;
  onRemoveClick?: () => void;
  showRulesAndTags?: boolean;
}

const actionBtnClass =
  'w-[30px] h-[30px] flex items-center justify-center rounded-full text-[#343437] hover:bg-gray-200 transition-colors';

const stopPropagation = (e: React.MouseEvent) => e.stopPropagation();

const createSyntheticEvent = () =>
  new MouseEvent('click', { bubbles: false }) as any;

const PlaylistItemPresentationActionsV2: FC<
  PlaylistItemPresentationActionsV2Props
> = ({
  onEditClick,
  onTagsClick,
  onDuplicateClick,
  onSeeInLibraryClick,
  seeInLibraryDisabled = false,
  seeInLibraryDisabledTooltip = 'Save changes before locating this content',
  onRemoveClick,
  showRulesAndTags,
}) => {
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const [moreTooltipOpen, setMoreTooltipOpen] = useState(false);
  const moreTriggerRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (isMoreOpen) setMoreTooltipOpen(false);
  }, [isMoreOpen]);

  const handleEditClick = useCallback(() => {
    onEditClick?.(createSyntheticEvent());
  }, [onEditClick]);

  const handleActionClick = useCallback((action?: () => void) => {
    if (!action) return;
    setIsMoreOpen(false);
    action();
  }, []);

  return (
    <div className="relative h-full flex items-center gap-0.5">
      {onEditClick && (
        <SidebarMenuTooltip title="Edit" placement="bottom" arrow>
          <div onClick={stopPropagation}>
            <Button
              variant="unstyled"
              className={actionBtnClass}
              onPress={handleEditClick}
            >
              <Edit size={16} />
            </Button>
          </div>
        </SidebarMenuTooltip>
      )}

      <SidebarMenuTooltip
        title="More actions"
        placement="bottom"
        arrow
        open={moreTooltipOpen && !isMoreOpen}
        disableHoverListener
        disableFocusListener
        disableTouchListener
      >
        <div
          onClick={stopPropagation}
          onMouseEnter={() => {
            if (!isMoreOpen) setMoreTooltipOpen(true);
          }}
          onMouseLeave={() => setMoreTooltipOpen(false)}
        >
          <Button
            variant="unstyled"
            className={actionBtnClass}
            onPress={() => setIsMoreOpen(true)}
          >
            <span ref={moreTriggerRef}>
              <Ellipsis size={16} />
            </span>
          </Button>

          <Popover
            triggerRef={moreTriggerRef}
            isOpen={isMoreOpen}
            onOpenChange={setIsMoreOpen}
            className="w-[194px]"
            placement="bottom end"
          >
            <Dialog className="px-0 py-2" aria-label="presentation actions">
              <div className="overflow-hidden">
                {showRulesAndTags && onTagsClick && (
                  <Button
                    className="w-full flex items-center gap-2 py-2.5 px-4 cursor-pointer hover:bg-gray-200 min-h-[40px]"
                    variant="unstyled"
                    onPress={() => handleActionClick(onTagsClick)}
                  >
                    <div className="flex shrink-0 items-center justify-center w-[17px] h-[17px]">
                      <Tag size={17} />
                    </div>
                    <span className="flex items-center pt-0.5 text-base leading-tight">
                      Tags
                    </span>
                  </Button>
                )}

                {onDuplicateClick && (
                  <Button
                    className="w-full flex items-center gap-2 py-2.5 px-4 cursor-pointer hover:bg-gray-200 min-h-[40px]"
                    variant="unstyled"
                    onPress={() => handleActionClick(onDuplicateClick)}
                  >
                    <div className="flex shrink-0 items-center justify-center w-[17px] h-[17px]">
                      <CopyIcon size={17} />
                    </div>
                    <span className="flex items-center pt-0.5 text-base leading-tight">
                      Duplicate
                    </span>
                  </Button>
                )}

                {(onSeeInLibraryClick || seeInLibraryDisabled) && (
                  <SidebarMenuTooltip
                    title={
                      seeInLibraryDisabled ? seeInLibraryDisabledTooltip : ''
                    }
                    placement="right"
                    arrow
                  >
                    <div>
                      <Button
                        className={cn(
                          'w-full flex items-center gap-2 py-2.5 px-4 min-h-[40px]',
                          seeInLibraryDisabled
                            ? 'opacity-40 cursor-not-allowed'
                            : 'cursor-pointer hover:bg-gray-200',
                        )}
                        variant="unstyled"
                        isDisabled={seeInLibraryDisabled}
                        onPress={() =>
                          seeInLibraryDisabled
                            ? undefined
                            : handleActionClick(onSeeInLibraryClick)
                        }
                      >
                        <div className="flex shrink-0 items-center justify-center w-[17px] h-[17px]">
                          <FolderMagnifyingGlassIcon size={17} />
                        </div>
                        <span className="flex items-center pt-0.5 text-base leading-tight">
                          See in Library
                        </span>
                      </Button>
                    </div>
                  </SidebarMenuTooltip>
                )}

                {onRemoveClick && (
                  <Button
                    className="w-full flex items-center gap-2 py-2.5 px-4 cursor-pointer hover:bg-gray-200 min-h-[40px]"
                    variant="unstyled"
                    onPress={() => handleActionClick(onRemoveClick)}
                  >
                    <div className="flex shrink-0 items-center justify-center w-[17px] h-[17px]">
                      <PlaylistRemoveIcon size={17} />
                    </div>
                    <span className="flex items-center pt-0.5 text-base leading-tight">
                      Remove
                    </span>
                  </Button>
                )}
              </div>
            </Dialog>
          </Popover>
        </div>
      </SidebarMenuTooltip>
    </div>
  );
};

export default PlaylistItemPresentationActionsV2;
